import { keyframes, Stack, styled } from "@mui/material";
import { useMemo } from "react";
import { matchPath, useLocation, useParams } from "react-router";
import { MeetingNotification } from "src/components";
import { Paths } from "src/constants";
import { useSettings } from "src/hooks";
import { OneOnOneMeetingNotificationDto, TeamMeetingNotificationDto, useGetActiveMeetingsForUser } from "src/http";

const MeetingNotificationsContainerAnimation = keyframes`
  0% { opacity: 0; transform: translateX(-50%) translateY(calc(100% + 20px)) }
  100% { opacity: 1; transform: translateX(-50%) translateY(0) }
`;

const MeetingNotificationsContainer = styled(Stack)`
  position: fixed;
  display: flex;
  left: 50%;
  bottom: 20px;
  opacity: 1;
  transform: translateX(-50%);
  z-index: 999;
  animation: ${MeetingNotificationsContainerAnimation} 300ms ease-in-out;
`;

interface UrlParams {
  slug: string;
}

const MeetingNotificationSnackbar = () => {
  const { slug } = useParams<keyof UrlParams>() as UrlParams;
  const { data } = useGetActiveMeetingsForUser();
  const { dismissedMeetingNotifications, updateDismissedMeetingNotifications } = useSettings();
  const location = useLocation();

  const ONE_DAY_MS = 24 * 60 * 60 * 1000;

  const handleDismiss = (instanceGuid: string) => {
    if (!instanceGuid) return;

    const now = Date.now();

    const newNotifications = dismissedMeetingNotifications
      .filter((item) => item.timestamp && now - item.timestamp < ONE_DAY_MS)
      .concat({ instanceGuid, timestamp: now });

    updateDismissedMeetingNotifications(newNotifications);
  };

  const dismissedInstanceIds = useMemo(() => {
    const now = Date.now();

    return dismissedMeetingNotifications
      .filter((n) => n.timestamp && now - n.timestamp < ONE_DAY_MS)
      .map((n) => n.instanceGuid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dismissedMeetingNotifications]);

  // Don't show notifications while in a meeting
  if (matchPath(Paths.Meeting, location.pathname)) {
    return null;
  }

  if (data === undefined) {
    return null;
  }

  return (
    <MeetingNotificationsContainer spacing={0.5}>
      {(
        data.teamMeetings.filter((teamMeeting) => {
          if (!!matchPath(Paths.TeamMeetings, location.pathname)) {
            return teamMeeting.team.slug !== slug;
          } else {
            return true;
          }
        }) as (OneOnOneMeetingNotificationDto | TeamMeetingNotificationDto)[]
      )
        .concat(data.oneOnOneMeetings)
        .filter((meeting) => meeting.currentInstance && !dismissedInstanceIds.includes(meeting.currentInstance.guid))
        .sort((a, b) => a.currentInstance!.startedAtDateTimeUtc.localeCompare(b.currentInstance!.startedAtDateTimeUtc))
        .map((meeting) => (
          <MeetingNotification
            key={meeting.currentInstance!.guid}
            meeting={meeting}
            onDismiss={() => handleDismiss(meeting.currentInstance!.guid)}
          />
        ))}
    </MeetingNotificationsContainer>
  );
};

export { MeetingNotificationSnackbar };
